<script>
import Swal from 'sweetalert2';
import { purchaseOrderToTicketData } from '@/serializers/PurchaseOrder.serializer';
import { mapGetters } from 'vuex';
import { FPUtils } from '@/utils/FloatingPointUtils';
import { PurchaseOrderProduct } from '@/models/PurchaseOrder/PurchaseOrderProduct';
import { FormUtils } from '@/utils/FormUtils';
import SaleSuccessActions from '@/components/POS/SaleSuccessActions.vue';
import InvoicesService from '../../services/InvoicesService';
import ProductService from '../../services/ProductService';
import PurchaseOrderService from '../../services/PurchaseOrderService';
import CurrencyFormatter from '../../models/CurrencyFormatter';
import PosStepPayment from './PosStepPayment.vue';
import PosStepDelivery from './PosStepDelivery.vue';
import PosStepInvoice from './PosStepInvoice.vue';

export default {
  name: 'pos-checkout-modal',
  components: {
    PosStepPayment,
    PosStepDelivery,
    PosStepInvoice,
    SaleSuccessActions,
  },
  data: () => ({
    currentStoreId: null,
    dialog: false,
    step: 1,
    totalSteps: 1,
    invoiceRequired: 'no',
    invoiceSwitchValue: false,
    isResolvingInvoiceToggle: false,
    block: 0,
    saleSuccessDialog: false,
    completedSaleId: null,
    completedSaleFoil: null,
    completedSaleHasCollection: false,
    completedSaleInvoiceError: '',
    completedSaleInvoiceResponse: null,
    completedSaleChange: null,
    completedSaleTotal: null,
    completedSaleClientEmail: '',
    reprintingSaleTicket: false,
  }),
  computed: {
    ...mapGetters({
      stompClient: 'stompClient',
      authUserData: 'authUserData',
      canViewPos: 'canViewPos',
      selectedClient: 'selectedClient',
      itemsList: 'itemsList',
      frontItemsList: 'frontItemsList',
      payments: 'payments',
      realPaidCash: 'realPaidCash',
      cartTotal: 'cartTotal',
      totalOrderPaid: 'totalOrderPaid',
      FTotalOrderPaid: 'FTotalOrderPaid',
      hasItemsWithNoInventory: 'hasItemsWithNoInventory',
      deliverySaleData: 'deliverySaleData',
      invoiceSaleData: 'invoiceSaleData',
    }),
    visibleStepSequence() {
      const sequence = [1];
      if (this.hasItemsWithNoInventory) sequence.push(2);
      if (this.invoiceRequired === 'yes') sequence.push(3);
      return sequence;
    },
    renderedStep() {
      const idx = this.visibleStepSequence.indexOf(this.step);
      return idx >= 0 ? idx + 1 : 1;
    },
    renderedTotalSteps() {
      return this.visibleStepSequence.length;
    },
    FRealPaidCash() {
      return CurrencyFormatter.format(this.realPaidCash);
    },
    FRemainingTotal() {
      if (this.remainingTotal > 0) {
        return CurrencyFormatter.format(this.remainingTotal);
      }
      return CurrencyFormatter.format(0);
    },
    cashChange() {
      const remaining = FPUtils.round2dec(this.remainingTotal);
      if (remaining >= 0) return 0;
      return FPUtils.round2dec(Math.abs(remaining));
    },
    FChange() {
      return CurrencyFormatter.format(this.cashChange);
    },
    remainingTotal() {
      const orderTotal = FPUtils.round2dec(Number(this.cartTotal.total || 0));
      const paidTotal = FPUtils.round2dec(Number(this.totalOrderPaid || 0));
      return FPUtils.round2dec(orderTotal - paidTotal);
    },
    isLastVisibleStep() {
      return this.step === this.visibleStepSequence[this.visibleStepSequence.length - 1];
    },
    finishOrderBtnText() {
      if (!this.isLastVisibleStep) {
        return 'Siguiente';
      }
      return 'Finalizar orden';
    },
  },
  watch: {
    async invoiceRequired(val) {
      if (val === 'yes') {
        this.$refs.posStepInvoice.preloadCatalogs();
      }
    },
    async realPaidCash() {
      if (this.invoiceRequired === 'yes') {
        this.$refs.posStepInvoice.applyDefaultPaymentConditions();
      }
    },
  },
  methods: {
    toSafeNumber(value) {
      return FormUtils.toSafeNumber(value);
    },
    formatCurrency(value) {
      return CurrencyFormatter.format(value);
    },
    resetInvoiceState() {
      this.invoiceRequired = 'no';
      this.invoiceSwitchValue = false;
    },
    syncTotalSteps() {
      this.totalSteps = this.renderedTotalSteps;
      if (!this.visibleStepSequence.includes(this.step)) {
        this.step = this.visibleStepSequence[this.visibleStepSequence.length - 1];
      }
    },
    async startPayment() {
      this.$store.dispatch('clearTotalOrderPaid');
      this.$store.dispatch('clearRealPaidCash');

      this.step = 1;
      this.dialog = true;
      this.syncTotalSteps();
    },
    removeCashPayment() {
      const cashPaid = this.toSafeNumber(this.realPaidCash);
      if (cashPaid <= 0) return;
      this.$store.dispatch('updateTotalOrderPaid', -cashPaid);
      this.$store.dispatch('clearRealPaidCash');
    },
    paymentMethodIdToName(id) {
      const mapping = {
        1: 'Efectivo',
        2: 'Tarjeta de débito',
        3: 'Tarjeta de crédito',
        5: 'Transferencia',
        6: 'Otro',
      };
      return mapping[id] || 'Desconocido';
    },
    removePayment(index) {
      this.$store.dispatch('removePayment', index);
    },
    resetEverything() {
      this.$refs.posStepPayment.resetPaymentState();
      this.resetInvoiceState();
    },
    returnToOrder() {
      if (this.payments.length > 0 || this.realPaidCash) {
        const swal = Swal.fire({
          allowOutsideClick: false,
          title: 'Regresar a orden',
          confirmButtonText: 'Si',
          text: '¿Desea regresar a la orden? Se eliminarán los pagos realizados',
          icon: 'warning',
          showCancelButton: true,
          cancelButtonText: 'No',
        });
        swal.then((resp) => {
          if (resp.value) {
            this.dialog = false;
            this.resetEverything();
          }
        });
      } else {
        this.dialog = false;
        this.resetEverything();
      }
    },
    previousStep() {
      if (this.step === 1) {
        this.returnToOrder();
        return;
      }
      const currentIndex = this.visibleStepSequence.indexOf(this.step);
      if (currentIndex > 0) {
        this.step = this.visibleStepSequence[currentIndex - 1];
        return;
      }
      const [firstStep] = this.visibleStepSequence;
      this.step = firstStep;
    },
    async enableInvoiceForm(value) {
      if (this.isResolvingInvoiceToggle) {
        this.invoiceSwitchValue = this.invoiceRequired === 'yes';
        return;
      }

      if (!value) {
        this.invoiceRequired = 'no';
        this.invoiceSwitchValue = false;
        this.syncTotalSteps();
        return;
      }

      this.isResolvingInvoiceToggle = true;
      try {
        const metadataReady = await this.$refs.posStepInvoice.ensurePaymentMetadata(true);
        this.invoiceRequired = metadataReady ? 'yes' : 'no';
        this.invoiceSwitchValue = metadataReady;
      } finally {
        this.isResolvingInvoiceToggle = false;
        this.syncTotalSteps();
      }
    },
    nextStep() {
      if (this.step === 1 && this.remainingTotal > 0) {
        Swal.fire('Saldo pendiente', 'Falta saldo por cubrir. Verifica los montos ingresados antes de finalizar.', 'warning');
        return;
      }

      if (this.step === 2 && this.hasItemsWithNoInventory) {
        const noInventoryValidation = this.$refs.posStepDelivery.validateForm();
        if (noInventoryValidation !== true) {
          return;
        }
        const products = [];
        this.itemsList.forEach((x) => {
          const product = new PurchaseOrderProduct(x.id, x.baseCost, x.calculatedDiscount);
          product.isGift = x.isGift;
          products.push(product);
        });
        this.$refs.posStepDelivery.saveData(products);
      }

      if (this.step === 3 && this.invoiceRequired === 'yes') {
        const invoiceValidation = this.$refs.posStepInvoice.validateInvoiceForm();
        if (invoiceValidation !== true) {
          Swal.fire('Error', invoiceValidation, 'error');
          return;
        }
      }

      const currentIndex = this.visibleStepSequence.indexOf(this.step);
      if (currentIndex < 0) {
        const [firstStep] = this.visibleStepSequence;
        this.step = firstStep;
        return;
      }
      if (currentIndex < this.visibleStepSequence.length - 1) {
        this.step = this.visibleStepSequence[currentIndex + 1];
        return;
      }
      this.finishOrder();
    },
    async finishOrder() {
      const productsTotal = [];
      let products = [];
      this.block = 1;

      try {
        const toSafeNumber = (v) => {
          const n = Number(v);
          return Number.isFinite(n) ? FPUtils.round2dec(n) : 0;
        };
        const trimMax = (v, max = 100) => String(v || '').trim().slice(0, max);
        const onlyDigits = (v) => String(v || '').replace(/\D/g, '');

        this.itemsList.forEach((x) => {
          const product = new PurchaseOrderProduct(x.id, x.baseCost, x.calculatedDiscount);
          product.isGift = x.isGift;
          products.push(product);
        });

        this.frontItemsList.forEach((item) => {
          productsTotal.push({ productId: item.id, total: item.quantity });
        });

        const paymentsPayload = this.payments
          .map((p) => ({
            totalImport: toSafeNumber(p.totalImport),
            paymentMethod: Number(p.paymentMethod),
            confirmationNumber: trimMax(p.confirmationNumber, 100) || null,
            cardLastDigits: onlyDigits(p.cardLastDigits).slice(0, 4) || null,
            cardBrand: trimMax(p.cardBrand, 100) || null,
            bankName: trimMax(p.bankName, 100) || null,
            change: toSafeNumber(p.change),
          }))
          .filter((p) => p.totalImport > 0 && [1, 2, 3, 4, 5, 6].includes(p.paymentMethod));

        const cashAmount = toSafeNumber(this.realPaidCash);
        const hasCashPayment = paymentsPayload.some((x) => x.paymentMethod === 1);
        if (cashAmount > 0 && !hasCashPayment) {
          paymentsPayload.push({
            totalImport: cashAmount,
            paymentMethod: 1,
            confirmationNumber: null,
            cardLastDigits: null,
            cardBrand: null,
            bankName: null,
            change: toSafeNumber(this.cashChange),
          });
        }

        if (paymentsPayload.length === 0) {
          Swal.fire('Atención', 'No hay pagos registrados. Añade un pago para poder finalizar la orden.', 'warning');
          this.block = 0;
          return;
        }

        const orderTotal = toSafeNumber(this.cartTotal.total);

        const nonCashPaid = FPUtils.round2dec(
          paymentsPayload.reduce((acc, p) => {
            if (p.paymentMethod === 1) return acc;
            return acc + toSafeNumber(p.totalImport);
          }, 0),
        );

        const cashPaid = FPUtils.round2dec(
          paymentsPayload.reduce((acc, p) => {
            if (p.paymentMethod !== 1) return acc;
            return acc + toSafeNumber(p.totalImport);
          }, 0),
        );

        const cashNeeded = FPUtils.round2dec(Math.max(0, orderTotal - nonCashPaid));
        const effectiveCashPaid = FPUtils.round2dec(Math.min(cashPaid, cashNeeded));

        const netPaidAmount = FPUtils.round2dec(nonCashPaid + effectiveCashPaid);

        if (FPUtils.round2dec(netPaidAmount - orderTotal) < 0) {
          Swal.fire('Saldo pendiente', 'Falta saldo por cubrir. Registra un pago adicional para completar la orden.', 'warning');
          this.block = 0;
          return;
        }

        if (this.remainingTotal > 0) {
          Swal.fire('Saldo pendiente', 'Falta saldo por cubrir. Registra un pago adicional para completar la orden.', 'warning');
          this.block = 0;
          return;
        }

        const { total } = this.cartTotal;
        const employeeId = this.authUserData.user_claims.employee;
        let clientId;
        let clientEmail;
        let clientDiscount = 0;

        if (this.selectedClient && this.selectedClient.id > 0) {
          clientId = this.selectedClient.id;
          clientDiscount = this.selectedClient.discount;
          if (this.selectedClient.contactInfo) {
            clientEmail = this.selectedClient.contactInfo.email;
          }
        }

        let delivery = null;
        if (this.hasItemsWithNoInventory) {
          delivery = this.deliverySaleData.client;
          products = this.deliverySaleData.products;
        }

        const resp = await PurchaseOrderService.finishPurchaseOrderV2(
          this.currentStoreId,
          employeeId,
          clientId,
          clientDiscount,
          total,
          paymentsPayload,
          products,
          productsTotal,
          delivery,
        );

        if (resp && resp.error) {
          Swal.fire({
            title: 'Error',
            html: `Hubo un error al registrar la venta:<br><br>${resp.message || 'Error desconocido'}`,
            icon: 'error',
          });
          this.block = 0;
          this.$store.dispatch('clearRealPaidCash');
          this.$store.dispatch('clearPayments');
          this.$store.dispatch('clearTotalOrderPaid');
          this.$store.dispatch('clearDeliverySaleData');
          this.$store.dispatch('clearInvoiceSaleData');
          this.dialog = false;
          this.resetInvoiceState();
          return;
        }

        const completedSaleId = resp?.data?.sale_order_id;
        const completedSaleFoil = resp?.data?.sale_order_foil;
        const completedSaleHasCollection = this.hasItemsWithNoInventory;
        const completedSaleChange = this.FChange;
        const completedSaleTotal = this.cartTotal.FTotal;

        if (this.savedQuotationPurchaseOrderId && this.savedQuotationPurchaseOrderId > 0) {
          const quotationOrderId = this.savedQuotationPurchaseOrderId;
          const response = await PurchaseOrderService.finishSavedPurchaseOrder(quotationOrderId);
          if (response && response.error) {
            Swal.fire('Error', 'Hubo un error al registrar los pagos', 'error');
            this.block = 0;
            return;
          }
          this.$store.dispatch('clearSavedQuotationItems');
        }

        // Impresion de Ticket
        await this.printTicket(completedSaleId);

        let invoiceResp = null;
        let invoiceError = null;
        if (this.invoiceRequired === 'yes') {
          try {
            const factura = await this.$refs.posStepInvoice.buildFacturaFromSale(completedSaleId);
            if (factura.error) {
              invoiceError = factura.error || 'Error desconocido';
            } else {
              invoiceResp = await InvoicesService.postFacturalaSaleInvoice(completedSaleId, factura);
            }
          } catch (e) {
            invoiceError = e?.message || e?.data?.message || 'No se pudo generar la factura';
          }
        }

        await this.completeOrder(
          completedSaleTotal,
          completedSaleChange,
          completedSaleId,
          completedSaleFoil,
          completedSaleHasCollection,
          clientEmail,
          invoiceResp?.data,
          invoiceError,
        );
      } catch (e) {
        console.error(e);
        Swal.fire('Error', 'Ocurrió un error inesperado al finalizar la orden.', 'error');
      } finally {
        this.block = 0;
      }
    },
    async completeOrder(
      completedSaleTotal,
      completedSaleChange,
      completedSaleId,
      completedSaleFoil,
      completedSaleHasCollection,
      clientEmail,
      invoiceResp,
      invoiceError,
    ) {
      this.$store.dispatch('clearOrder');
      this.$store.dispatch('clearTotalOrderPaid');
      this.$store.dispatch('clearItemsWithNoInventory');
      this.$store.dispatch('clearRealPaidCash');
      this.$store.dispatch('clearPayments');
      this.$store.dispatch('clearDeliverySaleData');
      this.$store.dispatch('clearInvoiceSaleData');
      this.resetInvoiceState();

      const { store } = this.authUserData.user_claims;
      const payload = await ProductService.getProductAutocompleteData('', 100, store, null);
      this.$store.dispatch('setProductsAutocomplete', payload);
      this.$store.dispatch('getClientsAutocomplete');

      this.completedSaleTotal = completedSaleTotal;
      this.completedSaleChange = completedSaleChange;
      this.completedSaleId = completedSaleId;
      this.completedSaleFoil = completedSaleFoil;
      this.completedSaleHasCollection = completedSaleHasCollection;
      this.completedSaleInvoiceError = invoiceError || '';
      this.completedSaleInvoiceResponse = invoiceResp || null;
      this.completedSaleClientEmail = clientEmail || '';
      this.saleSuccessDialog = true;
      await this.$nextTick();
      this.dialog = false;
      this.syncTotalSteps();
    },
    async printTicket(id) {
      const data = await PurchaseOrderService.getPurchaseOrderData(id);
      if (this.stompClient && this.stompClient.connected) {
        const ticketData = purchaseOrderToTicketData(data, this.ticketStruct);
        const payload = JSON.stringify(ticketData);
        this.stompClient.send('/exchange/orders/orders', payload, { 'content-type': 'application/json' });
      } else {
        // eslint-disable-next-line no-console
        console.error('no print client connected');
      }
    },
    async reprintCompletedSaleTicket() {
      if (!this.completedSaleId || this.reprintingSaleTicket) return;
      try {
        this.reprintingSaleTicket = true;
        await this.printTicket(this.completedSaleId);
      } catch {
        Swal.fire('Error', 'No se pudo reimprimir el ticket de venta.', 'error');
      } finally {
        this.reprintingSaleTicket = false;
      }
    },
    closeSaleSuccessDialog() {
      this.saleSuccessDialog = false;
      this.completedSaleId = null;
      this.completedSaleFoil = null;
      this.completedSaleHasCollection = false;
      this.completedSaleInvoiceError = '';
      this.completedSaleInvoiceResponse = null;
      this.completedSaleChange = null;
      this.completedSaleClientEmail = '';
    },
    downloadCompletedSaleInvoice() {
      const item = this.completedSaleInvoiceResponse;
      this.invoicePdf(item);
      this.invoiceXml(item);
    },
    getInvoiceId(item) {
      return item?.id || null;
    },
    invoicePdf(item) {
      const invoiceId = this.getInvoiceId(item);
      if (!invoiceId) return;
      window.open(`${process.env.VUE_APP_BASEURL}/invoice/${invoiceId}/pdf`, '_blank');
    },
    invoiceXml(item) {
      const invoiceId = this.getInvoiceId(item);
      if (!invoiceId) return;
      window.open(`${process.env.VUE_APP_BASEURL}/invoice/${invoiceId}/xml`, '_blank');
    },
  },
  mounted() {
    const { store } = this.authUserData.user_claims;
    this.currentStoreId = store;
  },
};
</script>

<template>
<div>
  <div class="d-flex justify-space-between align-center">
    <div class="d-flex flex-column align-center">
      <div class="text-center text-h6 text-lg-h5">Total a Pagar</div>
      <h1>{{ cartTotal.FTotal }}</h1>
    </div>
    <div v-if="canViewPos" class="d-flex">
      <v-btn
        v-if="itemsList.length > 0"
        @click="startPayment"
        block
        color="secondary"
        x-large>
        PAGAR
      </v-btn>
    </div>
  </div>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="1200px"
    transition="dialog-bottom-transition"
  >
    <v-card class="payment-dialog-card">
      <div class="custom-dialog-header">
        <div class="d-flex vertical-align-center">
          <v-icon color="primary">mdi-cash-register</v-icon>
          <span class="text-h5 font-weight-bold ml-2">Registro de Cobro y Entrega</span>
        </div>
        <div>
          <v-chip dense outlined color="primary">
            <span class="text-body-1">Paso {{ renderedStep }} de {{ renderedTotalSteps }}</span>
          </v-chip>
        </div>
      </div>
      <v-divider></v-divider>
      <div class="pa-4">
        <v-row>
          <v-col cols="12" sm="4">
            <div class="d-flex flex-column">
              <div style="border: 1px solid #e0e0e0; padding: 16px;">
                <div class="text-center text-h6 text-lg-h5">
                  <span class="text-caption">TOTAL A PAGAR</span>
                  <h1 class="text-h4 font-weight-bold text-total">{{ cartTotal.FTotal }}</h1>
                </div>
              </div>
              <div class="d-flex flex-column my-4">
                <div class="d-flex justify-space-between">
                  <span class="text-body-1">Total Pagado:</span>
                  <span><strong>{{ FTotalOrderPaid }}</strong></span>
                </div>
                <div class="d-flex justify-space-between">
                  <span class="text-body-1">Total Restante:</span>
                  <span
                    class="font-weight-bold"
                    :class="FRemainingTotal === '$0.00' ? 'text-change' : 'text-total-remaining'"
                    ><strong>{{ FRemainingTotal }}</strong></span>
                </div>
              </div>
              <v-divider></v-divider>
              <div class="d-flex justify-space-between my-6 mx-2 text-change-size">
                <span class="text-change-size">Cambio:</span>
                <span class="text-change"><strong>{{ FChange }}</strong></span>
              </div>
              <div v-if="(realPaidCash > 0 || payments.length > 0) && step == 1">
                <span class="text-body-1 font-weight-bold">ABONOS REGISTRADOS:</span>
                <div>
                  <v-chip
                    v-if="realPaidCash > 0"
                    class="ma-1"
                    color="primary"
                    outlined
                  >
                    <span class="mr-2">Efectivo:</span>
                    <span class="font-weight-bold">{{ FRealPaidCash }}</span>
                    <v-btn small icon @click="removeCashPayment" class="ml-2">
                      <v-icon small>mdi-close-circle</v-icon>
                    </v-btn>
                  </v-chip>
                  <v-chip
                    v-for="(p, idx) in payments"
                    :key="idx"
                    class="ma-1"
                    color="primary"
                    outlined
                  >
                    <span class="mr-2">{{ paymentMethodIdToName(p.paymentMethod) }}:</span>
                    <span class="font-weight-bold">{{ formatCurrency(p.totalImport) }}</span>
                    <v-btn small icon @click="removePayment(idx)" class="ml-2">
                      <v-icon small>mdi-close-circle</v-icon>
                    </v-btn>
                  </v-chip>
                </div>
              </div>
            </div>
          </v-col>
          <v-col cols="12" sm="8">
            <v-window v-model="step">
              <v-window-item :value="1">
                <pos-step-payment
                  ref="posStepPayment"
                ></pos-step-payment>
              </v-window-item>
              <v-window-item :value="2">
                <pos-step-delivery
                  ref="posStepDelivery"
                  :current-step="step"
                  :pickup-store-id="currentStoreId"
                ></pos-step-delivery>
              </v-window-item>
              <v-window-item :value="3" eager>
                <pos-step-invoice
                  ref="posStepInvoice"
                  :invoice-required="invoiceRequired"
                ></pos-step-invoice>
              </v-window-item>
            </v-window>
          </v-col>
        </v-row>
      </div>
      <v-divider></v-divider>
      <div class="py-2 px-2 d-flex justify-space-between align-center">
        <v-btn @click="previousStep">
          <span v-if="step === 1">Cancelar</span>
          <span v-else>Regresar</span>
        </v-btn>
        <div class="d-flex align-center">
          <v-switch
            v-model="invoiceSwitchValue"
            @change="enableInvoiceForm"
            v-if="remainingTotal <= 0"
            label="Requiere Facturar"
            class="mr-4"
            :loading="isResolvingInvoiceToggle"
            :disabled="isResolvingInvoiceToggle"
            dense
          >
            <v-icon class="mr-2">mdi-checkbox-blank-outline</v-icon>
          </v-switch>
          <v-btn
            color="primary"
            @click="nextStep"
            :disabled="block === 1"
          >{{ finishOrderBtnText }}</v-btn>
        </div>
      </div>
      <v-overlay
        absolute
        :value="block === 1"
        color="white"
        opacity="0.85"
      >
        <div class="d-flex flex-column align-center justify-center">
          <v-progress-circular
            indeterminate
            color="primary"
            size="56"
            width="5"
          />
          <span class="mt-4 text-subtitle-1 font-weight-medium">
            Finalizando orden...
          </span>
        </div>
      </v-overlay>
    </v-card>
  </v-dialog>
  <v-dialog
    v-model="saleSuccessDialog"
    persistent
    max-width="640px"
  >
    <sale-success-actions
      :sale-id="completedSaleId"
      :sale-folio="completedSaleFoil"
      :client-email="completedSaleClientEmail"
      :show-collection-ticket="completedSaleHasCollection"
      :printing-ticket="reprintingSaleTicket"
      :invoice-response="completedSaleInvoiceResponse"
      :invoice-error-message="completedSaleInvoiceError"
      :cash-change="completedSaleChange"
      :total="completedSaleTotal"
      @reprint-ticket="reprintCompletedSaleTicket"
      @download-invoice="downloadCompletedSaleInvoice"
      @close="closeSaleSuccessDialog"
    />
  </v-dialog>
</div>
</template>

<style lang="less">
.custom-dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
}
.text-total {
  font-size: 2rem;
  font-weight: bold;
  color: #0d1d4e !important;
}
.text-total-remaining {
  color: #d32f2f; /* Red color for emphasis */
}
.text-change {
  color: #388e3c; /* Green color for emphasis */
}
.text-change-size {
  font-size: 24px;
}
</style>
